import { InferGetStaticPropsType, NextPage } from "next";
import { Section, SectionContainer } from "@/components/Sections";
import { FAQ_STATIC, FaqSection } from "@/_staticContent/faq";
import { Markdown } from "@/components/Markdown";
import Link from "@/components/Link";
import config from "../config";
import { Head } from "@/components/Head";
import Button from "@/components/Button";

export const getStaticProps = async () => {
  return {
    props: {
      faqStatic: FAQ_STATIC,
    },
    revalidate: config.staticRevalidationTime,
  } as const;
};

function getFaqSectionId(faqSection: FaqSection): string {
  return faqSection.header.toLowerCase();
}

type Props = InferGetStaticPropsType<typeof getStaticProps>;
const FaqPage: NextPage<Props> = function (props) {
  return (
    <>
      <Head
        title="FAQ"
        description="View the answers to Frequently Asked Questions."
      />
      <Section isFirstSection color={"blue"} fillScreenHeight={false}>
        <SectionContainer defaultPadding>
          <h1 className="text-style-headline2 mb-md">
            Frequently Asked Questions
          </h1>
          <div className="grid grid-cols-4 gap-lg">
            <p className="col-span-full tablet:col-span-2 desktop:col-span-3 tablet:order-2 text-style-body2 mb-md">
              Check the below sections to find all the information regarding the
              ECIU University experience. Can’t find the answer to your question
              here, or need additional information, please contact us via this{" "}
              <Link href="/contact">contact form</Link> and we are happy to help
              you.
            </p>
            <div className="col-span-full tablet:col-span-2 desktop:col-span-1 flex flex-col gap-y-sm bg-light-blue rounded-2 p-md">
              <h2 className="text-style-headline3">Table of contents</h2>
              {props.faqStatic.map((faqSection, index) => {
                return (
                  <Link
                    key={index}
                    className="text-style-link2"
                    href={`#${getFaqSectionId(faqSection)}`}
                  >
                    {faqSection.header}
                  </Link>
                );
              })}
            </div>
          </div>
        </SectionContainer>
      </Section>
      {props.faqStatic.map((faqSection, index) => {
        return (
          <Section
            key={index}
            id={getFaqSectionId(faqSection)}
            adjustScrollMarginToStickyObjectHeaderLinks={false}
            sectionPadding={false}
            className="even:bg-grey-10 odd:bg-light-yellow text-text-primary"
          >
            <SectionContainer grid>
              <div className="col-span-full laptop-lg:col-span-8">
                <h2 className="text-style-headline2">{faqSection.header}</h2>
                {faqSection.questionsAndAnswers.map((qa, index) => {
                  return (
                    <div key={index} className="my-lg">
                      <h3 className="text-style-headline3 mb-sm">
                        {qa.question}
                      </h3>
                      <Markdown content={qa.answer} />
                    </div>
                  );
                })}
              </div>
            </SectionContainer>
          </Section>
        );
      })}
      <div className="fixed bottom-sm right-sm laptop-lg:bottom-10 laptop-lg:right-10">
        <Button
          className={"shadow-elevation-2"}
          onClick={() => {
            window.scrollTo(0, 0);
          }}
        >
          Back to top
        </Button>
      </div>
    </>
  );
};

export default FaqPage;
